import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import { useQuery, useMutation } from '@apollo/client';
import { getAdminBanners } from '../../graphql/queries';
import { reorderBanners } from '../../graphql/mutation';
import { Card, Table, Button, Tag } from 'antd';
import { PlusOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { showSnackBar } from '../../components/SnackBar/SnackBar';
import { TargetingRulesSummary } from '../../components/TargetingRulesEditor';
import './Banners.css';

const audienceColors = {
  ALL: 'blue',
  FREE_USER: 'orange',
  PRO_USER: 'green',
  ACTIVE_SUB_NO_CREDITS: 'red',
};

const SortableRow = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#fafafa' } : {}),
  };

  return <tr {...props} ref={setNodeRef} style={style} {...attributes} />;
};

const DragHandle = ({ id }) => {
  const { listeners } = useSortable({ id });
  return (
    <HolderOutlined
      style={{ cursor: 'grab', color: '#999' }}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const Banners = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const [orderedBanners, setOrderedBanners] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const { data, loading } = useQuery(getAdminBanners, {
    variables: { page, pageSize },
    fetchPolicy: 'network-only',
  });

  const [reorderBannersMutation] = useMutation(reorderBanners);

  useEffect(() => {
    if (data?.getAdminBanners?.banners) {
      setOrderedBanners(data.getAdminBanners.banners);
    }
  }, [data]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = orderedBanners.findIndex((b) => b.id === active.id);
    const newIndex = orderedBanners.findIndex((b) => b.id === over.id);
    const newOrder = arrayMove(orderedBanners, oldIndex, newIndex);

    setOrderedBanners(newOrder);

    try {
      await reorderBannersMutation({
        variables: { ids: newOrder.map((b) => b.id) },
      });
      showSnackBar('success', 'Reordered', 'Banner group order saved');
    } catch (error) {
      setOrderedBanners(orderedBanners);
      showSnackBar('error', 'Error', error.message || 'Failed to reorder banners');
    }
  };

  const columns = [
    {
      title: '',
      key: 'drag',
      width: 40,
      render: (_, record) => <DragHandle id={record.id} />,
    },
    {
      title: '#',
      width: 50,
      render: (_, __, index) => <span style={{ color: '#999' }}>{index + 1}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Audience',
      key: 'audience',
      render: (_, record) => (
        <TargetingRulesSummary
          targetingRules={record.targetingRules}
          audienceType={record.audienceType}
        />
      ),
    },
    {
      title: 'Items',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 70,
    },
    {
      title: 'Schedule',
      key: 'schedule',
      render: (_, record) => {
        if (!record.startDate && !record.endDate) return <Tag>Always</Tag>;
        const start = record.startDate ? new Date(record.startDate).toLocaleDateString() : '...';
        const end = record.endDate ? new Date(record.endDate).toLocaleDateString() : '...';
        const now = new Date();
        const isExpired = record.endDate && new Date(record.endDate) < now;
        const isUpcoming = record.startDate && new Date(record.startDate) > now;
        return (
          <span>
            {start} - {end}{' '}
            {isExpired && <Tag color="red">Expired</Tag>}
            {isUpcoming && <Tag color="orange">Upcoming</Tag>}
          </span>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div className="banners-container">
      <Card>
        <div className="table-header">
          <h2>Banner Groups</h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/banners/create')}
          >
            Create Banner Group
          </Button>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedBanners.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              columns={withRowLink(columns, (record) => `/banners/${record.id}`, { exclude: ['drag'] })}
              dataSource={orderedBanners}
              rowKey="id"
              loading={loading}
              components={{
                body: { row: SortableRow },
              }}
              onRow={(record) => ({
                ...getRowLinkProps(`/banners/${record.id}`, () => navigate(`/banners/${record.id}`)),
              })}
              pagination={{
                current: page + 1,
                pageSize,
                total: data?.getAdminBanners?.count || 0,
                onChange: (p) => setPage(p - 1),
              }}
            />
          </SortableContext>
        </DndContext>
      </Card>
    </div>
  );
};

export default Banners;
